import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import type { AccessibleFarmerProgramme } from "model/useDashboardQueries";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { useMemo, useState } from "react";
import type {
  MilkQualityPerFarmerPeriodData,
  PerFarmerActionsData,
  PerFarmerPcfData,
} from "../hooks/useInsightsDashboardQueries";
import {
  useMilkQualityPerFarmerPeriod,
  usePerFarmerActions,
  usePerFarmerPcf,
  useProgrammeInfo,
} from "../hooks/useInsightsDashboardQueries";
import { getSccZone, getTbcZone, getZoneColor } from "../milkQualityThresholds";
import FarmerDetailDialog from "./FarmerDetailDialog";

function TrendIcon({ trend }: { trend: Trend }) {
  const sx = { fontSize: 14, ml: 0.25 };
  if (trend === "up")
    return <TrendingUpIcon sx={{ ...sx, color: COLORS.dahlia[900] }} />;
  if (trend === "down")
    return <TrendingDownIcon sx={{ ...sx, color: COLORS.dahlia[500] }} />;
  return <TrendingFlatIcon sx={{ ...sx, color: "#a1a1aa" }} />;
}

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
});

const GRID_COLS_DAIRY =
  "minmax(120px, 2fr) 0.9fr 0.9fr 1.2fr 0.8fr 0.8fr 1.2fr";
const GRID_COLS_DEFAULT = "minmax(120px, 2.5fr) 0.9fr 0.9fr 1.2fr 1.2fr";

function fmt(value: number | undefined | null, maxDecimals = 1): string {
  if (value == null) return "-";
  const num = Number(value);
  if (isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

type Trend = "up" | "down" | "flat";

export type ProgrammeStatus = "invited" | "onboarding" | "actioning";

export type FarmerRow = {
  guid: string;
  name: string;
  joinedDate: string | null;
  status: ProgrammeStatus;
  pcf: number | null;
  pcfDeltaPct: number | null;
  pcfTrend: Trend;
  actionsCommitted: number | null;
  actionsOnTrack: number | null;
  actionsCompleted: number | null;
  latestScc: number | null;
  latestTbc: number | null;
  sccTrend: Trend;
  tbcTrend: Trend;
};

const STATUS_LABELS: Record<ProgrammeStatus, string> = {
  invited: "Invited",
  onboarding: "Onboarding",
  actioning: "Actioning",
};

const STATUS_COLORS: Record<ProgrammeStatus, { bg: string; fg: string }> = {
  invited: { bg: "#f4f4f5", fg: "#71717a" },
  onboarding: { bg: "#e0f2fe", fg: "#0369a1" },
  actioning: { bg: "#dcfce7", fg: "#15803d" },
};

function StatusBadge({ status }: { status: ProgrammeStatus }) {
  const { bg, fg } = STATUS_COLORS[status];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: 999,
        backgroundColor: bg,
        color: fg,
        fontSize: "0.7rem",
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      {STATUS_LABELS[status]}
    </Box>
  );
}

function computeTrend(latest: number, previous: number | undefined): Trend {
  if (previous === undefined) return "flat";
  if (latest > previous * 1.05) return "up";
  if (latest < previous * 0.95) return "down";
  return "flat";
}

type MilkPerFarmer = {
  scc: number | null;
  tbc: number | null;
  sccTrend: Trend;
  tbcTrend: Trend;
};

function getLatestMilkPerFarmer(
  data: MilkQualityPerFarmerPeriodData[] | undefined
): Map<string, MilkPerFarmer> {
  const byFarmer = new Map<string, MilkQualityPerFarmerPeriodData[]>();
  for (const r of data ?? []) {
    const uid = r["MilkQuality.userGuid"];
    if (!byFarmer.has(uid)) byFarmer.set(uid, []);
    byFarmer.get(uid)?.push(r);
  }

  const result = new Map<string, MilkPerFarmer>();
  for (const [uid, records] of byFarmer) {
    const sorted = records.sort((a, b) =>
      String(a["MilkQuality.periodStart"]).localeCompare(
        String(b["MilkQuality.periodStart"])
      )
    );
    const latest = sorted[sorted.length - 1];
    const previous = sorted.length > 1 ? sorted[sorted.length - 2] : undefined;

    const scc = Number(latest["MilkQuality.somaticCellCount"]);
    const tbc = Number(latest["MilkQuality.totalBacterialCount"]);

    result.set(uid, {
      scc: scc === 0 ? null : scc,
      tbc: tbc === 0 ? null : tbc,
      sccTrend:
        scc === 0
          ? "flat"
          : computeTrend(
              scc,
              previous
                ? Number(previous["MilkQuality.somaticCellCount"])
                : undefined
            ),
      tbcTrend:
        tbc === 0
          ? "flat"
          : computeTrend(
              tbc,
              previous
                ? Number(previous["MilkQuality.totalBacterialCount"])
                : undefined
            ),
    });
  }
  return result;
}

export function buildFarmerRows(
  farmers: AccessibleFarmerProgramme[],
  actionsData: PerFarmerActionsData[] | undefined,
  pcfData: PerFarmerPcfData[] | undefined,
  milkData: MilkQualityPerFarmerPeriodData[] | undefined,
  selectedYear: number | null
): FarmerRow[] {
  const actionsMap = new Map<string, PerFarmerActionsData>();
  (actionsData ?? []).forEach((r) => {
    actionsMap.set(r["SubmittedActions.userGuid"], r);
  });

  // Build per-farmer year -> value map for PCF
  const pcfByUser = new Map<string, Map<number, number>>();
  (pcfData ?? []).forEach((r) => {
    const userGuid = r["Emissions.userGuid"];
    const year = Number(r["Emissions.year"]);
    const value = Number(r["Emissions.avgValue"]);
    if (!Number.isFinite(year) || !Number.isFinite(value)) return;
    let yearMap = pcfByUser.get(userGuid);
    if (!yearMap) {
      yearMap = new Map();
      pcfByUser.set(userGuid, yearMap);
    }
    yearMap.set(year, value);
  });

  const milkMap = getLatestMilkPerFarmer(milkData);

  return farmers.map((f) => {
    const guid = f.userGuid;
    const actions = actionsMap.get(guid);
    const uniqueActions = actions
      ? Number(actions["SubmittedActions.uniqueActionCount"])
      : null;
    const actionsOnTrack = actions
      ? Number(actions["SubmittedActions.uniqueActionsOnTrack"])
      : null;
    const actionsCompleted = actions
      ? Number(actions["SubmittedActions.uniqueActionsCompleted"])
      : null;
    const milk = milkMap.get(guid);

    let pcf: number | null = null;
    let pcfDeltaPct: number | null = null;
    let pcfTrend: Trend = "flat";
    if (selectedYear != null) {
      const yearMap = pcfByUser.get(guid);
      const current = yearMap?.get(selectedYear);
      if (current != null) {
        pcf = current;
        const previous = yearMap?.get(selectedYear - 1);
        if (previous != null && previous !== 0) {
          pcfDeltaPct = ((current - previous) / previous) * 100;
          if (pcfDeltaPct > 0.5) pcfTrend = "up";
          else if (pcfDeltaPct < -0.5) pcfTrend = "down";
          else pcfTrend = "flat";
        }
      }
    }

    let status: ProgrammeStatus;
    if ((uniqueActions ?? 0) > 0) status = "actioning";
    else if (f.consentSigned) status = "onboarding";
    else status = "invited";

    return {
      guid,
      name: f.userName || f.userEmail,
      joinedDate: f.joinedDate,
      status,
      pcf,
      pcfDeltaPct,
      pcfTrend,
      actionsCommitted: uniqueActions,
      actionsOnTrack,
      actionsCompleted,
      latestScc: milk?.scc ?? null,
      latestTbc: milk?.tbc ?? null,
      sccTrend: milk?.sccTrend ?? "flat",
      tbcTrend: milk?.tbcTrend ?? "flat",
    };
  });
}

function formatJoinedDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function FarmerTable() {
  const { data: farmersData, isLoading: farmersLoading } =
    useAccessibleFarmerProgrammes();
  const { data: actionsData, isLoading: actionsLoading } =
    usePerFarmerActions();
  const { data: pcfData, isLoading: pcfLoading } = usePerFarmerPcf();
  const { data: milkData, isLoading: milkLoading } =
    useMilkQualityPerFarmerPeriod();
  const { data: programmeInfo } = useProgrammeInfo();

  const isDairy =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] === "DAIRY";

  const farmers = farmersData ?? [];

  const [search, setSearch] = useState("");
  const [selectedFarmer, setSelectedFarmer] = useState<{
    guid: string;
    name: string;
  } | null>(null);

  const availableYears = useMemo(() => {
    const set = new Set<number>();
    (pcfData?.data ?? []).forEach((r) => {
      const y = Number(r["Emissions.year"]);
      if (Number.isFinite(y) && y > 1900) set.add(y);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [pcfData?.data]);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const effectiveYear =
    selectedYear ?? (availableYears.length > 0 ? availableYears[0] : null);

  const isLoading =
    farmersLoading || actionsLoading || pcfLoading || milkLoading;

  const rows = useMemo(
    () =>
      buildFarmerRows(
        farmers,
        actionsData?.data,
        pcfData?.data,
        milkData?.data,
        effectiveYear
      ),
    [farmers, actionsData?.data, pcfData?.data, milkData?.data, effectiveYear]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(term));
  }, [rows, search]);

  const aggregates = useMemo(() => {
    const totalPcf = filteredRows.reduce((sum, r) => sum + (r.pcf ?? 0), 0);
    const farmersWithPcf = filteredRows.filter((r) => r.pcf != null).length;
    const avgPcf = farmersWithPcf > 0 ? totalPcf / farmersWithPcf : null;
    const totalActionsCommitted = filteredRows.reduce(
      (sum, r) => sum + (r.actionsCommitted ?? 0),
      0
    );
    const totalActionsOnTrack = filteredRows.reduce(
      (sum, r) => sum + (r.actionsOnTrack ?? 0),
      0
    );
    const totalActionsCompleted = filteredRows.reduce(
      (sum, r) => sum + (r.actionsCompleted ?? 0),
      0
    );
    const actioningCount = filteredRows.filter(
      (r) => r.status === "actioning"
    ).length;
    return {
      count: filteredRows.length,
      avgPcf,
      totalActionsCommitted,
      totalActionsOnTrack,
      totalActionsCompleted,
      actioningCount,
    };
  }, [filteredRows]);

  if (!isLoading && farmers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        No farmers found in your programme.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search farmers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#a1a1aa", fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: 360,
            "& .MuiOutlinedInput-root": {
              borderRadius: `${BORDER_RADIUS.small}px`,
              backgroundColor: "white",
            },
          }}
        />
      </Box>

      <Box>
        <Card>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isDairy
                ? GRID_COLS_DAIRY
                : GRID_COLS_DEFAULT,
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              alignItems: "start",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#71717a", lineHeight: 1.2 }}
            >
              Farmer
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#71717a",
                textAlign: "right",
                lineHeight: 1.2,
              }}
            >
              Joined
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#71717a",
                textAlign: "right",
                lineHeight: 1.2,
              }}
            >
              Status
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.25,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#71717a", lineHeight: 1.2 }}
              >
                Product carbon footprint
              </Typography>
              {availableYears.length > 1 ? (
                <Select
                  size="small"
                  value={effectiveYear ?? ""}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  variant="standard"
                  disableUnderline
                  renderValue={(v) => `Year: ${v}`}
                  sx={{
                    fontSize: "0.7rem",
                    color: "#a1a1aa",
                    "& .MuiSelect-select": {
                      py: 0,
                      pl: 0,
                      pr: "18px !important",
                    },
                    "& .MuiSelect-icon": { fontSize: 14, color: "#a1a1aa" },
                  }}
                >
                  {availableYears.map((y) => (
                    <MenuItem key={y} value={y} sx={{ fontSize: "0.7rem" }}>
                      Year {y}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                effectiveYear != null && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#a1a1aa", fontSize: "0.7rem" }}
                  >
                    Year: {effectiveYear}
                  </Typography>
                )
              )}
            </Box>
            {isDairy && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#71717a",
                  textAlign: "right",
                  lineHeight: 1.2,
                }}
              >
                SCC
              </Typography>
            )}
            {isDairy && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#71717a",
                  textAlign: "right",
                  lineHeight: 1.2,
                }}
              >
                TBC
              </Typography>
            )}
            <Box
              sx={{
                textAlign: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#71717a", lineHeight: 1.2 }}
                >
                  Actions
                </Typography>
                <Tooltip
                  title="committed / on track / done"
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: "#a1a1aa",
                      cursor: "help",
                      display: { xs: "block", sm: "none" },
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#a1a1aa",
                  fontSize: "10px",
                  display: { xs: "none", sm: "block" },
                }}
              >
                committed / on track / done
              </Typography>
            </Box>
          </Box>

          {isLoading
            ? [1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isDairy
                      ? GRID_COLS_DAIRY
                      : GRID_COLS_DEFAULT,
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #f4f4f5",
                  }}
                >
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                  <Skeleton
                    variant="text"
                    width={70}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                  {isDairy && (
                    <Skeleton
                      variant="text"
                      width={40}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  )}
                  {isDairy && (
                    <Skeleton
                      variant="text"
                      width={40}
                      height={20}
                      sx={{ ml: "auto" }}
                    />
                  )}
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    sx={{ ml: "auto" }}
                  />
                </Box>
              ))
            : filteredRows.map((row) => (
                <Box
                  key={row.guid}
                  onClick={() =>
                    setSelectedFarmer({ guid: row.guid, name: row.name })
                  }
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isDairy
                      ? GRID_COLS_DAIRY
                      : GRID_COLS_DEFAULT,
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #f4f4f5",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: COLORS.dahlia[50] },
                    "&:last-child": { borderBottom: "none" },
                    transition: "background-color 0.15s",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#18181b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#3f3f46",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatJoinedDate(row.joinedDate)}
                  </Typography>
                  <Box sx={{ textAlign: "right" }}>
                    <StatusBadge status={row.status} />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.25,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.pcf != null ? (
                      <>
                        <Typography variant="body2" sx={{ color: "#3f3f46" }}>
                          {fmt(row.pcf)}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "#a1a1aa", ml: 0.5 }}
                          >
                            kg CO₂e/kg
                          </Typography>
                        </Typography>
                        {row.pcfDeltaPct != null && (
                          <>
                            <TrendIcon trend={row.pcfTrend} />
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  row.pcfTrend === "up"
                                    ? COLORS.dahlia[900]
                                    : row.pcfTrend === "down"
                                    ? COLORS.dahlia[500]
                                    : "#a1a1aa",
                                fontWeight: 600,
                              }}
                            >
                              {row.pcfDeltaPct > 0 ? "+" : ""}
                              {row.pcfDeltaPct.toFixed(1)}%
                            </Typography>
                          </>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#3f3f46" }}>
                        -
                      </Typography>
                    )}
                  </Box>
                  {isDairy && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.25,
                      }}
                    >
                      {row.latestScc != null ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: `${getZoneColor(
                                getSccZone(row.latestScc)
                              )}18`,
                              color: getZoneColor(getSccZone(row.latestScc)),
                            }}
                          >
                            {fmt(row.latestScc, 0)}
                          </Typography>
                          <TrendIcon trend={row.sccTrend} />
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#a1a1aa" }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  )}
                  {isDairy && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.25,
                      }}
                    >
                      {row.latestTbc != null ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: `${getZoneColor(
                                getTbcZone(row.latestTbc)
                              )}18`,
                              color: getZoneColor(getTbcZone(row.latestTbc)),
                            }}
                          >
                            {fmt(row.latestTbc, 0)}
                          </Typography>
                          <TrendIcon trend={row.tbcTrend} />
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: "#a1a1aa" }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#3f3f46",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmt(row.actionsCommitted, 0)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#a1a1aa", mx: 0.25 }}
                    >
                      /
                    </Typography>
                    {fmt(row.actionsOnTrack, 0)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#a1a1aa", mx: 0.25 }}
                    >
                      /
                    </Typography>
                    {fmt(row.actionsCompleted, 0)}
                  </Typography>
                </Box>
              ))}

          {!isLoading && filteredRows.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No farmers match your search.
              </Typography>
            </Box>
          )}

          {!isLoading && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isDairy
                  ? GRID_COLS_DAIRY
                  : GRID_COLS_DEFAULT,
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: "2px solid #e5e5e5",
                backgroundColor: "#f5f5f5",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#18181b" }}
              >
                Total ({aggregates.count})
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#a1a1aa", textAlign: "right" }}
              >
                -
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#3f3f46",
                  textAlign: "right",
                  fontSize: "0.7rem",
                }}
              >
                {aggregates.actioningCount}/{aggregates.count} actioning
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#3f3f46", textAlign: "right" }}
              >
                {aggregates.avgPcf != null ? (
                  <>
                    {fmt(aggregates.avgPcf)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#a1a1aa", ml: 0.5 }}
                    >
                      avg
                    </Typography>
                  </>
                ) : (
                  "-"
                )}
              </Typography>
              {isDairy && (
                <Typography
                  variant="body2"
                  sx={{ color: "#a1a1aa", textAlign: "right" }}
                >
                  -
                </Typography>
              )}
              {isDairy && (
                <Typography
                  variant="body2"
                  sx={{ color: "#a1a1aa", textAlign: "right" }}
                >
                  -
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: "#3f3f46",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {fmt(aggregates.totalActionsCommitted, 0)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#a1a1aa", mx: 0.25 }}
                >
                  /
                </Typography>
                {fmt(aggregates.totalActionsOnTrack, 0)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#a1a1aa", mx: 0.25 }}
                >
                  /
                </Typography>
                {fmt(aggregates.totalActionsCompleted, 0)}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      <FarmerDetailDialog
        open={selectedFarmer !== null}
        farmerGuid={selectedFarmer?.guid ?? null}
        farmerName={selectedFarmer?.name ?? null}
        onClose={() => setSelectedFarmer(null)}
      />
    </>
  );
}
